// The dashboard's icon set — INLINE stroke SVGs, 1.7 stroke weight, rounded joins, single tone drawn in
// `currentColor` (design brief §3 icons: "inline stroke SVGs, 1.7 weight, rounded joins, single tone; no
// icon fonts, no emoji"). Recreated natively from the prototype's icon vocabulary (brand/ritona-app-handoff
// app.js ICONS) as a typed React component — no icon font, no third-party icon library (a dependency the
// §6 policy would gate; a hand-drawn inline set is smaller and governed).
//
// Every glyph is authored in the 24×24 viewBox the prototype uses. Sizing is via width/height props; colour
// is always inherited (`stroke="currentColor"`), so an icon takes the token-derived text colour of its
// context — never a raw hue (G-181).

/** The stable icon names the shell references. Extend here as later surfaces need more glyphs. */
export type IconName =
  | 'today'
  | 'tasks'
  | 'kpis'
  | 'clock'
  | 'approvals'
  | 'memory'
  | 'files'
  | 'report'
  | 'team'
  | 'pulse'
  | 'plug'
  | 'grid'
  | 'search'
  | 'chat'
  | 'x'
  | 'send'
  | 'compass'
  | 'chevron'
  | 'chevleft'
  | 'plus'
  | 'settings'
  | 'check'
  | 'mail'
  | 'info'
  | 'upload'
  | 'db'
  | 'chevdown'
  | 'ban'
  | 'trash'
  | 'terminal'
  | 'history'
  | 'expand'
  | 'shrink'
  | 'trend-up'
  | 'trend-down'
  | 'trend-flat';

// The path geometry for each glyph, in the 24×24 viewBox. Kept as raw path/shape markup (identical geometry
// to the prototype) so the drawn line stays byte-faithful to the design source; React renders it inside the
// stroke-configured <svg> below via dangerouslySetInnerHTML — the content is a static, in-repo constant
// (never user input), so there is no injection surface.
const GLYPHS: Readonly<Record<IconName, string>> = {
  today:
    '<rect x="3.5" y="5" width="17" height="15" rx="2.5"/><path d="M3.5 9.5h17"/><path d="M8 3.5v3M16 3.5v3"/><circle cx="12" cy="14.5" r="1.5" fill="currentColor" stroke="none"/>',
  tasks:
    '<rect x="3.5" y="4.5" width="6" height="15" rx="1.5"/><rect x="14.5" y="4.5" width="6" height="10" rx="1.5"/>',
  kpis: '<path d="M4 4.5v15h16"/><path d="M7.5 15.5l3.3-3.8 3 2.4L20 7.5"/>',
  clock: '<circle cx="12" cy="12" r="8"/><path d="M12 7.5V12l3 2"/>',
  approvals:
    '<path d="M12 3.5l7 2.5v5c0 4.2-2.9 7.4-7 8.5-4.1-1.1-7-4.3-7-8.5V6z"/><path d="M9 11.5l2.2 2.2 4.3-4.2"/>',
  memory:
    '<path d="M12 6.6C10.5 5.4 8.4 4.9 5.5 5.1v12c2.9-.2 5 .3 6.5 1.5 1.5-1.2 3.6-1.7 6.5-1.5v-12C15.6 4.9 13.5 5.4 12 6.6z"/><path d="M12 6.6v12"/>',
  files:
    '<path d="M7 3.5h6L18 8.5V19.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1z"/><path d="M12.5 3.5V9h5"/>',
  report:
    '<path d="M7 3.5h6L18 8.5V19.5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1z"/><path d="M12.5 3.5V9h5"/><path d="M8.7 16.9l2.1-2.5 1.7 1.3 2.7-3.2"/>',
  team: '<circle cx="9" cy="9" r="3"/><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5"/><path d="M16 6.4a3 3 0 0 1 0 5.4"/><path d="M17.5 14.3c1.9.6 3.3 2.3 3.3 4.7"/>',
  pulse: '<path d="M3.5 12h4l2-5.5 3.2 11L15 12h5.5"/>',
  plug: '<path d="M8 2.5v5M16 2.5v5"/><path d="M6.5 7.5h11v2.8a5.5 5.5 0 0 1-11 0z"/><path d="M12 15.8V21"/>',
  grid: '<rect x="4" y="4" width="6.5" height="6.5" rx="1.3"/><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.3"/><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.3"/><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.3"/>',
  search: '<circle cx="11" cy="11" r="6.5"/><path d="M19.8 19.8l-4.2-4.2"/>',
  chat: '<path d="M4.5 5.5h15a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H10l-4 3.5V15.5H4.5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z"/>',
  x: '<path d="M6 6l12 12M18 6L6 18"/>',
  send: '<path d="M20.5 4L3.5 11l6.2 2.4L12 19.5z"/><path d="M20.5 4L9.7 13.4"/>',
  compass:
    '<path d="M12 3l8.5 4.8v8.4L12 21l-8.5-4.8V7.8z"/><path d="M12 3v18M3.5 7.8l8.5 4.8 8.5-4.8"/>',
  chevron: '<path d="M9 6l6 6-6 6"/>',
  chevleft: '<path d="M14 6l-6 6 6 6"/>',
  plus: '<path d="M12 5.5v13M5.5 12h13"/>',
  settings:
    '<path d="M4 7h8.5M17.5 7h2.5M4 17h2.5M11.5 17h8.5"/><circle cx="15" cy="7" r="2.3"/><circle cx="9" cy="17" r="2.3"/>',
  check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
  mail: '<rect x="3.5" y="5.5" width="17" height="13" rx="1.8"/><path d="M4.5 7.5l7.5 5.8 7.5-5.8"/>',
  info: '<circle cx="12" cy="12" r="8.5"/><path d="M12 11.5v4.5"/><circle cx="12" cy="8" r="0.7" fill="currentColor" stroke="none"/>',
  upload:
    '<path d="M12 15.5V4.5"/><path d="M7.5 9l4.5-4.5L16.5 9"/><path d="M4.5 15.5v3a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1v-3"/>',
  db: '<ellipse cx="12" cy="6" rx="7" ry="2.6"/><path d="M5 6v12c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V6"/><path d="M5 12c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6"/>',
  chevdown: '<path d="M6 9.5l6 6 6-6"/>',
  ban: '<circle cx="12" cy="12" r="8.5"/><path d="M6 6l12 12"/>',
  trash:
    '<path d="M4.5 7h15"/><path d="M9 7V4.5h6V7"/><path d="M7 7l.8 13h8.4L17 7"/><path d="M10 10.5v6M14 10.5v6"/>',
  terminal: '<path d="M5 7.5l4.8 4.5L5 16.5"/><path d="M12.5 17h6"/>',
  history:
    '<path d="M4.5 5.5v4.5H9"/><path d="M5 14a7.5 7.5 0 1 0 .9-6.4"/><path d="M12 8.5V12l2.5 1.5"/>',
  expand:
    '<path d="M14 5h5v5"/><path d="M19 5l-6.5 6.5"/><path d="M10 19H5v-5"/><path d="M5 19l6.5-6.5"/>',
  shrink:
    '<path d="M19.5 9.5h-5v-5"/><path d="M14.5 9.5L20 4"/><path d="M4.5 14.5h5v5"/><path d="M9.5 14.5L4 20"/>',
  'trend-up': '<path d="M5 17L11 11l3 3 5-6"/><path d="M14.5 8H19v4.5"/>',
  'trend-down': '<path d="M5 7l6 6 3-3 5 6"/><path d="M14.5 16H19v-4.5"/>',
  'trend-flat': '<path d="M5 12h14"/>',
};

export interface IconProps {
  readonly name: IconName;
  /** Pixel size (width = height). Defaults to 18 — the nav/topbar size. Decorative unless labelled. */
  readonly size?: number;
  /** Extra utility classes (token-derived only, e.g. a text-colour utility that sets currentColor). */
  readonly className?: string;
}

/** Render one line icon at the 1.7 stroke weight, inheriting `currentColor` (design brief §3). */
export function Icon({ name, size = 18, className }: IconProps): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      dangerouslySetInnerHTML={{ __html: GLYPHS[name] }}
    />
  );
}
